import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { APIError, getContext, isAdminContext } from 'src/api';
import { orderingCalc, submitBooking } from 'src/api/cart';
import i18next from 'src/config/i18next';
import { ROUTE_ADMIN_OFFICE_CUSTOMER_PROFILE_ORDERVIEW } from 'src/config/routes';
import CartRequestDTO from 'src/entities/cart/request/CartRequestDTO';
import SelectedProduct from 'src/entities/cart/request/SelectedProduct';
import ShoppingCart from 'src/entities/cart/response/ShoppingCart';
import {
    ADD_CART,
    CHANGE_PRODUCT_CUSTOM_FIELD_VALUE,
    CHANGE_PRODUCT_QUANTITY,
    CLEAR_SHOPPING_CART,
    failureOrderingCalc,
    FAILURE_ORDERING_CALC,
    ORDER_AGAIN,
    REMOVE_ALL_FROM_CART,
    REMOVE_FROM_CART,
    requestOrderingCalc,
    SUBMIT_BOOKING,
    successOrderingCalc,
    UPDATE_SHOPPING_CART,
} from 'src/store/cart/actions';
import { getShoppingCart, shoppingCartDefaultState, ShoppingCartEntityState } from 'src/store/cart/selectors';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';

/**
 * Adds product to the shopping cart
 */
function* add(action: AnyAction): SagaIterator {
    const newProducts: SelectedProduct[] = action.data.filter(product => product.quantity > 0) ?? [];
    if (newProducts.length === 0) return;

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);

    // If the cart currently contains an order directly product then notify the user
    if (existingShoppingCart.content.containsOrderDirectlyProduct) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('translations:order_table.error_contains_order_directly_product'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        return;
    }

    yield put(requestOrderingCalc());

    // If product is a order directly product, remove all other items present in the cart
    const cartRequestDTO = handleCartRequestDTO().fromShoppingCartAndNew(
        action.orderDirectly ? ShoppingCart.emptyCart() : existingShoppingCart.content,
        newProducts
    );
    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) {
        yield put(failureOrderingCalc(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(successOrderingCalc(response, action.orderDirectly));
        action.successCb?.();
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('translations:order_table.success_add'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

function* remove(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart = yield select(getShoppingCart);

    const cartRequestDTO = handleCartRequestDTO().fromShoppingCartWithoutItem(
        existingShoppingCart.content,
        action.product
    );

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, false));
}

function* removeAll(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart = yield select(getShoppingCart);

    const cartRequestDTO = CartRequestDTO.fromShoppingCartWithoutItems(existingShoppingCart);

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, false));
}

function* changeProductQuantity(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const { product, quantity } = action;

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);

    const cartRequestDTO = handleCartRequestDTO().fromShoppingCartWithProductQuantityChange(
        existingShoppingCart.content,
        product,
        quantity
    );

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else
        yield put(
            successOrderingCalc(
                response,
                quantity === 0 ? false : existingShoppingCart.content.containsOrderDirectlyProduct
            )
        );
}

function* changeProductCustomFieldValue(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const { product, fieldName, value } = action;

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);

    const cartRequestDTO = CartRequestDTO.fromShoppingCartWithProductCustomFieldValueChange(
        existingShoppingCart.content,
        product,
        fieldName,
        value
    );

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, false));
}

export function* updateShoppingCart(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartRequestDTO = handleCartRequestDTO().fromShoppingCart(
        existingShoppingCart.content,
        action.data,
        action.fromLandingPage,
        action.fromLandingPageId
    );

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, existingShoppingCart.content.containsOrderDirectlyProduct));
}

export function* clearShoppingCart(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const emptyShoppingCart = shoppingCartDefaultState();
    const cartRequestDTO = CartRequestDTO.fromShoppingCart(emptyShoppingCart.content);

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) {
        yield put(failureOrderingCalc(error));
        if (action.onError) action.onError();
    } else {
        yield put(successOrderingCalc(response, false));
        if (action.onSuccess) action.onSuccess();
    }
}

export function* orderAgain(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const cartRequestDTO = handleCartRequestDTO().fromExistingOrder(action.data);
    console.log('cart: ', cartRequestDTO);

    const { error, response } = yield call(orderingCalc, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, action.orderDirectly));
}

/**
 * Handles submit booking
 */
function* submitBookingSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartRequestDTO = handleCartRequestDTO().FromShoppingCartWithSubmitBookingFormData(
        existingShoppingCart.content,
        action.data
    );

    const { error, response } = yield call(submitBooking, cartRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else {
        if (isAdminContext()) {
            window.location.replace(ROUTE_ADMIN_OFFICE_CUSTOMER_PROFILE_ORDERVIEW.path);
        } else {
            window.location.replace(response.data);
        }
    }
}

const handleCartRequestDTO = () => {
    return CartRequestDTO;
};

/**
 * Extracts the error message from an ordering calc failure and displays a notification
 */
function* orderingCalcFailureSaga(action: AnyAction): SagaIterator {
    const apiError = action.error as APIError;
    yield put(
        enqueueSnackbarAction({
            message: apiError.axios.response?.data?.message ?? apiError.message ?? apiError.code,
            options: {
                key: new Date().getTime() + Math.random(),
                variant: 'error',
            },
        })
    );
}

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/

/**
 * Add to cart watcher
 */
export function* generateAddCartWatcher() {
    yield takeLatest(ADD_CART, add);
}

/**
 * Delete item from cart watcher
 */
export function* generateRemoveFromCartWatcher() {
    yield takeLatest(REMOVE_FROM_CART, remove);
}

/**
 * Delete all item from cart watcher
 */
export function* generateRemoveAllFromCartWatcher() {
    yield takeLatest(REMOVE_ALL_FROM_CART, removeAll);
}

/**
 * Change Quantity of a Product within shopping cart watcher
 */
export function* generateChangeProductQuantityInCartWatcher() {
    yield takeLatest(CHANGE_PRODUCT_QUANTITY, changeProductQuantity);
}

/**
 * Change Custom Fields of a Product within shopping cart watcher
 */
export function* generateChangeProductCustomFieldValueInCartWather() {
    yield takeLatest(CHANGE_PRODUCT_CUSTOM_FIELD_VALUE, changeProductCustomFieldValue);
}

/**
 * Change Quantity of a Product within shopping cart watcher
 */
export function* generateUpdateShoppingCartWatcher() {
    yield takeLatest(UPDATE_SHOPPING_CART, updateShoppingCart);
}

/**
 * Change Quantity of a Product within shopping cart watcher
 */
export function* generateClearShoppingCartWatcher() {
    yield takeLatest(CLEAR_SHOPPING_CART, clearShoppingCart);
}

/**
 * Submit Booking Watcher
 */
export function* generateSubmitBookingWatcher() {
    yield takeLatest(SUBMIT_BOOKING, submitBookingSaga);
}

/**
 * Watches ordering calc failures
 */
export function* generateOrderingCalcFailureWatcher() {
    yield takeLatest(FAILURE_ORDERING_CALC, orderingCalcFailureSaga);
}

export function* generateOrderAgainWatcher() {
    yield takeLatest(ORDER_AGAIN, orderAgain);
}
